const rawApi = import.meta.env.VITE_API_URL ?? "/api";
/** Never call plain HTTP from HTTPS pages — relative /api via nginx is the prod default. */
const API =
  typeof rawApi === "string" && rawApi.startsWith("http://") ? "/api" : rawApi;

export type AdminPermKey =
  | "overview"
  | "users"
  | "bugs"
  | "settings"
  | "catalog"
  | "vehicles"
  | "world"
  | "einsatzzugang";

export type AdminPermissions = Partial<Record<AdminPermKey, boolean>>;

export type AdminRole = "owner" | "tool" | null;

export const ADMIN_PERM_KEYS: AdminPermKey[] = [
  "overview",
  "users",
  "bugs",
  "settings",
  "catalog",
  "vehicles",
  "world",
  "einsatzzugang",
];

export const ADMIN_PERM_LABELS: Record<AdminPermKey, string> = {
  overview: "Übersicht",
  users: "Benutzer",
  bugs: "Bugs",
  settings: "Einstellungen",
  catalog: "Katalog",
  vehicles: "Fahrzeuge",
  world: "Welt-Tools",
  einsatzzugang: "Einsatzzugang (Katalog-Editor)",
};

export type AuthUser = {
  id: string;
  username: string;
  email: string;
  isAdmin: boolean;
  playAllowed: boolean;
  adminRole?: AdminRole;
  permissions?: AdminPermissions;
};

export type BugReport = {
  id: string;
  userId?: string | null;
  username: string;
  message: string;
  page: string;
  status: "open" | "done" | string;
  adminNote?: string | null;
  playerReply?: string | null;
  playerNotified?: number;
  createdAt: string | Date;
  resolvedAt?: string | Date | null;
  resolvedBy?: string | null;
};

export type OrgPersonnelNeed = {
  count: number;
  trainings: string[];
};

export type CustomIncidentDraft = {
  title: string;
  summary: string;
  summaryAlt?: string;
  keyword?: string;
  category?: string;
  tags?: string[];
  severity: number;
  sizeClass: string;
  minLevel?: number;
  required: Record<string, number>;
  rewardBudget: number;
  rewardBudgetMin?: number;
  rewardBudgetMax?: number;
  workMs: number;
  orgs: string[];
  minStations?: { fire?: number; police?: number; ems?: number; thw?: number };
  poiKinds?: string[];
  locationGroups?: string[];
  specialFlags?: string[];
  poolType?: "normal" | "planned";
  expandTo?: Array<{ templateId: string; chance: number }>;
  iconTemplateId?: string;
  waterNeed?: number;
  waterLitersRequired?: number;
  waterIgnoresHydrants?: boolean;
  requiredPersonnel?: {
    fire?: OrgPersonnelNeed;
    police?: OrgPersonnelNeed;
    ems?: OrgPersonnelNeed;
    thw?: OrgPersonnelNeed;
  };
  terrainTags?: string[];
  notes?: string;
  /** Autoren-Notizen zu Inhalten (Patienten, Funk, Props) — noch nicht runtime */
  contentNotes?: string;
};

export type CustomIncidentItem = {
  id: string;
  creatorId: string;
  templateId: string;
  status: "draft" | "published" | "pending_review" | "approved" | "rejected";
  draft: CustomIncidentDraft;
  notes: string;
  reviewNotes: string;
  reviewedBy?: string | null;
  reviewedAt?: string | Date | null;
  createdAt: string | Date;
  updatedAt: string | Date;
};

export type CustomIncidentSuggestResult = {
  suggestions: Partial<CustomIncidentDraft>;
  explanation: string[];
  economy: {
    rewardBudget: number;
    workMs: number;
    expectedPayoutEarly: number;
    expectedPayoutMid: number;
    payoutNote: string;
  };
};

export type CustomVehicleDraft = {
  label: string;
  typeKey: string;
  price: number;
  org: string;
  unlockLevelMin: number;
  slotClass: string;
  size: string;
  hoseM: number;
  waterTankLiters: number;
  foamMax: number;
  tools: number;
  rcCapacity: number;
  abCapacity: number;
  loadCapacityKg: number;
  buildingKinds: string[];
  patientCapacity: number;
  crewMin: number;
  crewMax: number;
  requiredPersonnelCount: number;
  requiredTrainings: string[];
  notes?: string;
  importedFromLive?: boolean;
};

export type CustomVehicleItem = {
  id: string;
  creatorId: string;
  vehicleKey: string;
  status: "draft" | "published" | "pending_review" | "approved" | "rejected";
  draft: CustomVehicleDraft;
  notes: string;
  reviewNotes: string;
  reviewedBy?: string | null;
  reviewedAt?: string | Date | null;
  createdAt: string | Date;
  updatedAt: string | Date;
};

export type CustomTrainingDraft = {
  name: string;
  trainingKey: string;
  description: string;
  org: string;
  durationMin: number;
  cost: number;
  roles: string[];
  skillDelta: number;
  notes?: string;
};

export type CustomTrainingItem = {
  id: string;
  creatorId: string;
  trainingKey: string;
  status: "draft" | "published" | "pending_review" | "approved" | "rejected";
  draft: CustomTrainingDraft;
  notes: string;
  reviewNotes: string;
  reviewedBy?: string | null;
  reviewedAt?: string | Date | null;
  createdAt: string | Date;
  updatedAt: string | Date;
};

export type CustomEventFamily = "world_event" | "citizen_request" | "phone_call";
export type CustomEventStatus = "draft" | "needs_fields" | "ready";

export type CustomEventDraft = {
  family: CustomEventFamily;
  eventKey: string;
  title: string;
  body: string;
  icon: string;
  minLevel: number;
  durationMs: number;
  worldKind?: "roadblock" | "major_event" | "storm" | "vip";
  severity?: number;
  etaPenalty?: number;
  radiusKm?: number;
  followUpChance?: number;
  followUpIncidentId?: string;
  citizenKind?: string;
  costBudget?: number;
  renownDelta?: number;
  influenceDelta?: number;
  fromLabel?: string;
  phoneKind?: "anruf" | "sprechwunsch" | "status5" | "support" | "custody";
  spawnWeight?: number;
  spawnChanceDenominatorMs?: number;
  maxConcurrent?: number;
  requireTutorialStep?: number;
  requireBuildingKinds?: string[];
  requireOrgs?: string[];
  requireCompetence?: string;
  rewardBudget?: number;
  rewardXp?: number;
  rewardRenown?: number;
  rewardInfluence?: number;
  notes?: string;
  importedFromLive?: boolean;
};

export type CustomEventItem = {
  id: string;
  creatorId: string;
  eventKey: string;
  family: CustomEventFamily;
  status: CustomEventStatus;
  draft: CustomEventDraft;
  notes: string;
  createdAt: string | Date;
  updatedAt: string | Date;
};

export type Snapshot = {
  world: {
    id: string;
    name: string;
    cityName: string;
    centerLat: number;
    centerLng: number;
    radiusKm: number;
    cityRadiusKm?: number;
    budget: number;
    renown: number;
    influence: number;
    tutorialStep: number;
    playerLevel?: number;
    xp?: number;
    hasLeitstelle?: boolean;
    loanDebt?: number;
    /** GeoJSON Polygon der Stadt-/Kreisgrenze */
    boundaryJson?: string | null;
    /** Nachbarkreis auf derselben Karte */
    neighborBoundaryJson?: string | null;
    neighborDistrictName?: string | null;
    /** Freigeschaltet: Spawn/Bau/Alarm im Nachbar */
    neighborUnlocked?: boolean;
    neighborUnlockHint?: string;
    neighborUnlockLevel?: number;
    /** Aktive Kalender-Saison (spielbarer Bonus) */
    season?: {
      id: string;
      labelDe: string;
      bannerDe: string;
      resolveBudgetBonusPct: number;
      resolveXpBonusPct: number;
      rankingMultiplier: number;
    } | null;
    /** Saison-Ranking Lite (eigene Punkte + Regeln) */
    seasonRanking?: {
      seasonId: string | null;
      labelDe: string | null;
      points: number;
      rankingMultiplier: number;
      breakdown: Array<{ id: string; labelDe: string; points: number; scope: string }>;
      rules: Array<{ id: string; labelDe: string; points: number; scope: string }>;
      previewActive: boolean;
      archive?: Array<{ seasonId: string; labelDe: string; points: number }>;
      endNoteDe?: string | null;
    } | null;
    coverageKm?: number;
    regionKind?: "city" | "kreis" | "fantasy";
    skillPoints?: number;
    competences?: string[];
    playerRankLabel?: string;
    startOrg?: "fire" | "police" | "ems";
    story?: {
      done: string[];
      activeId: string | null;
      active: {
        id: string;
        chapter: number;
        title: string;
        brief: string;
        objective: string;
        fromLabel: string;
        rewardBudget: number;
        rewardRenown: number;
        rewardInfluence: number;
        unlockHint?: string;
      } | null;
      progressLabel: string | null;
      grantsCompleted?: number;
      signedContracts?: string[];
      redispatchCount?: number;
      recruitCampaignCount?: number;
      fehlbestandCount?: number;
      hireCount?: number;
      autoDispatchCount?: number;
      manualDispatchCount?: number;
      autoRelianceMalusAt?: number;
      trainingGatesHardAt?: number;
      stationsInNeighbor?: number;
      railZonesReady?: boolean;
      grant?: {
        status: string;
        baselineResolved: number;
        needIncidents: number;
        payout: number;
        influencePaid: number;
      } | null;
    };
  };
  radioCalls?: Array<{
    id: string;
    kind: string;
    title: string;
    body: string;
    fromLabel: string;
    incidentId: string | null;
    status: string;
  }>;
  hospitalPatients?: Array<{
    id: string;
    buildingId: string;
    incidentId?: string | null;
    label: string;
    severity: string;
    specialty: string;
    status: string;
    rewardBudget: number;
    queueDeadlineAt: string | number;
    dischargeAt?: string | number | null;
  }>;
  custodyDetainees?: Array<{
    id: string;
    buildingId: string;
    incidentId?: string | null;
    label: string;
    cellIndex: number;
    status: string;
    mode: string;
    releaseAt: string | number;
  }>;
  buildings: Array<{
    id: string;
    kind: string;
    subtype: string;
    name: string;
    lat: number;
    lng: number;
    placement: string;
    slots: number;
    trailerSlots?: number;
    abSlots?: number;
    rcSlots?: number;
    modulesJson?: string;
    level?: number;
  }>;
  vehicles: Array<{
    id: string;
    buildingId: string;
    type: string;
    callsign: string;
    status: string;
    lat: number;
    lng: number;
    incidentId: string | null;
    routeJson: string | null;
    routeProgress: number;
    etaMs?: number | null;
    crew?: number;
    condition?: number;
    equipmentJson?: string;
    maintenanceUntil?: string | number | null;
  }>;
  personnel: Array<{
    id: string;
    buildingId: string | null;
    vehicleId?: string | null;
    name: string;
    role: string;
    skill: number;
    morale: number;
    status: string;
    wage?: number;
  }>;
  applicants?: Array<{
    id: string;
    name: string;
    role: string;
    skill: number;
    morale: number;
    wageAsk: number;
    source: string;
    expiresAt: string | number;
    createdAt?: string | number | Date;
    recruitStatus?: "applied" | "interviewing" | "interview_done";
    interviewUntil?: string | number | Date | null;
    interviewQuality?: "good" | "fair" | "poor" | null;
    revealedSkill?: number | null;
  }>;
  events?: Array<{
    id: string;
    kind: string;
    title: string;
    body: string;
    lat: number;
    lng: number;
    status: string;
    severity: number;
    etaPenalty: number;
    expiresAt: string | number;
  }>;
  citizenRequests?: Array<{
    id: string;
    kind: string;
    title: string;
    body: string;
    status: string;
    costBudget: number;
    renownDelta: number;
    influenceDelta: number;
    expiresAt: string | number;
  }>;
  incidents: Array<{
    id: string;
    templateId?: string;
    title: string;
    summary: string;
    severity: number;
    lat: number;
    lng: number;
    status: string;
    requiredJson: string;
    progress: number;
    rewardBudget: number;
    workDurationMs?: number;
    phase?: string;
    supportJson?: string;
    expiresAt?: string | number | null;
    timing?: {
      phase: string;
      phaseLabel: string;
      etaFirstArriveMs?: number | null;
      etaAllOnsceneMs?: number | null;
      etaArriveMs: number | null;
      etaReadyMs?: number | null;
      etaWorkLeftMs: number | null;
      etaDoneMs: number | null;
      requirementMet: boolean;
      missingRequirements?: string[];
      unitsEnroute: number;
      unitsOnscene: number;
      workProgress: number;
      partialWork?: boolean;
    };
  }>;
  todos: Array<{
    id: string;
    kind: string;
    title: string;
    body: string;
    done: boolean;
  }>;
};

async function req<T>(path: string, opts: RequestInit = {}, token?: string | null): Promise<T> {
  const headers: Record<string, string> = {
    ...(opts.headers as Record<string, string>),
  };
  const hasBody = opts.body != null && opts.body !== "";
  if (hasBody && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? res.statusText);
  return data as T;
}

export const api = {
  health: () =>
    req<{
      ok: boolean;
      mode?: string;
      registration?: boolean;
      devLogin?: { login: string; password: string; hint?: string };
    }>("/health"),
  cities: () =>
    req<{
      cities: Array<{ name: string; lat: number; lng: number; regionKind?: string }>;
      kreise?: Array<{ name: string; lat: number; lng: number; regionKind?: string }>;
      fantasy?: Array<{ name: string; lat: number; lng: number; regionKind?: string }>;
    }>("/meta/cities"),
  geocode: (q: string, mode: "city" | "kreis" | "auto" = "auto") =>
    req<{
      results: Array<{
        label: string;
        cityName: string;
        lat: number;
        lng: number;
        countryCode: string | null;
        regionKind?: string;
      }>;
    }>(`/meta/geocode?q=${encodeURIComponent(q)}&mode=${mode}`),
  register: (body: {
    email: string;
    username: string;
    password: string;
    acceptTerms: true;
  }) =>
    req<{
      token: string;
      user: AuthUser;
      message?: string | null;
    }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  login: (login: string, password: string) =>
    req<{ token: string; user: AuthUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password }),
    }),
  me: (token: string) => req<{ user: AuthUser; mode: string }>("/auth/me", {}, token),
  forgotPassword: (email: string) =>
    req<{ ok: boolean; mailConfigured: boolean; message: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  changePassword: (token: string, currentPassword: string, newPassword: string) =>
    req<{ ok: boolean }>(
      "/auth/password",
      { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) },
      token,
    ),
  reportBug: (token: string, body: { message: string; page?: string }) =>
    req<{ ok: boolean; id: string }>("/bugs", { method: "POST", body: JSON.stringify(body) }, token),
  bugMine: (token: string) =>
    req<{
      items: BugReport[];
      unread: number;
    }>("/bugs/mine", {}, token),
  bugInbox: (token: string) =>
    req<{
      items: Array<{
        id: string;
        message: string;
        status: string;
        adminNote?: string | null;
        playerReply?: string | null;
        resolvedBy?: string | null;
        createdAt: string;
      }>;
    }>("/bugs/inbox", {}, token),
  bugAck: (token: string, id: string) =>
    req<{ ok: boolean }>(`/bugs/${id}/ack`, { method: "POST", body: "{}" }, token),
  bugReply: (token: string, id: string, message: string) =>
    req<{ ok: boolean }>(`/bugs/${id}/reply`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }, token),
  adminOverview: (token: string) =>
    req<{
      counts: {
        users: number;
        pendingPlay: number;
        admins: number;
        openBugs: number;
        bugsTotal: number;
      };
      recentUsers: Array<{
        id: string;
        username: string;
        email: string;
        playAllowed: boolean;
        adminRole: AdminRole;
        createdAt: string;
      }>;
      recentBugs: BugReport[];
    }>("/admin/overview", {}, token),
  adminBugs: (token: string, status: "open" | "done" | "all" = "open") =>
    req<{ bugs: BugReport[] }>(`/admin/bugs?status=${status}`, {}, token),
  adminPatchBug: (
    token: string,
    id: string,
    body: { status?: "open" | "done"; adminNote?: string },
  ) =>
    req<{ bug: BugReport }>(`/admin/bugs/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }, token),
  adminUsers: (token: string) =>
    req<{
      users: Array<
        AuthUser & {
          createdAt?: string;
          adminRole?: AdminRole;
          permissions?: AdminPermissions;
        }
      >;
    }>("/admin/users", {}, token),
  adminPatchUser: (
    token: string,
    id: string,
    body: Partial<{
      playAllowed: boolean;
      isAdmin: boolean;
      adminRole: AdminRole;
      permissions: AdminPermissions;
      username: string;
      email: string;
    }>,
  ) =>
    req<{ user: AuthUser }>(`/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }, token),
  adminSetPassword: (token: string, id: string, password: string) =>
    req<{ ok: boolean }>(`/admin/users/${id}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }, token),
  adminSettings: (token: string) =>
    req<{
      registration_open: boolean;
      auto_approve_play: boolean;
      max_player_save_slots: number;
      season_preview?: string;
      season_options?: Array<{ id: string; labelDe: string }>;
      mail_configured: boolean;
      mode: string;
    }>("/admin/settings", {}, token),
  adminPatchSettings: (
    token: string,
    body: Partial<{
      registration_open: boolean;
      auto_approve_play: boolean;
      max_player_save_slots: number;
      season_preview: "" | "summer" | "halloween" | "winter" | "spring";
    }>,
  ) =>
    req<{
      registration_open: boolean;
      auto_approve_play: boolean;
      max_player_save_slots: number;
      season_preview?: string;
      season_options?: Array<{ id: string; labelDe: string }>;
      mail_configured: boolean;
      mode?: string;
    }>("/admin/settings", {
      method: "PATCH",
      body: JSON.stringify(body),
    }, token),
  adminCatalog: (token: string) =>
    req<{
      incidents: any[];
      overrides: any[];
      stats: { total: number; overridden: number; reviewed: number };
    }>("/admin/catalog", {}, token),
  adminVehicles: (token: string) =>
    req<{
      vehicles: Array<{
        id: string;
        label: string;
        price: number;
        crew: number;
        slotClass: string;
        size: string;
        hoseM: number;
        tankMax: number;
        foamMax: number;
        tools: number;
        rcCapacity: number;
        org: string;
        overridden?: boolean;
      }>;
    }>("/admin/vehicles", {}, token),
  adminVehicleSave: (token: string, type: string, body: Record<string, unknown>) =>
    req<{ ok: boolean; vehicle: unknown }>(
      `/admin/vehicles/${encodeURIComponent(type)}`,
      { method: "PUT", body: JSON.stringify(body) },
      token,
    ),
  adminVehicleReset: (token: string, type: string) =>
    req<{ ok: boolean; vehicle: unknown }>(
      `/admin/vehicles/${encodeURIComponent(type)}`,
      { method: "DELETE" },
      token,
    ),
  adminSaveCatalog: (
    token: string,
    templateId: string,
    body: {
      patch: Record<string, unknown>;
      enabled?: boolean;
      reviewed?: boolean;
      notes?: string;
    },
  ) =>
    req<{ ok: boolean }>(`/admin/catalog/${encodeURIComponent(templateId)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }, token),
  adminImportSeed: (token: string, mode: "merge" | "reset_unreviewed" | "force") =>
    req<{ deleted: number; mode: string }>("/admin/catalog/import-seed", {
      method: "POST",
      body: JSON.stringify({ mode }),
    }, token),
  worlds: (token: string) =>
    req<{
      worlds: Snapshot["world"][];
      maxSlots?: number;
      activeWorldId?: string | null;
    }>("/worlds", {}, token),
  createWorld: (
    token: string,
    body: {
      cityName: string;
      lat: number;
      lng: number;
      worldName?: string;
      regionKind?: "city" | "kreis" | "fantasy";
      startOrg?: "fire" | "police" | "ems";
      slotIndex?: number;
    },
  ) =>
    req<Snapshot>("/worlds", { method: "POST", body: JSON.stringify(body) }, token),
  ackRadio: (
    token: string,
    worldId: string,
    callId: string,
    opts?: {
      claimCustody?: boolean;
      cell?: number;
      custodyMode?: "short" | "standard" | "decline";
      ackDecision?: "proceed" | "reinforce" | "document" | "contain";
      funkPriority?: "nachalarm" | "halten" | "doku";
    },
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/radio/${callId}/ack`,
      {
        method: "POST",
        body: JSON.stringify({
          claimCustody: opts?.claimCustody ?? false,
          cell: opts?.cell,
          custodyMode: opts?.custodyMode,
          ackDecision: opts?.ackDecision,
          funkPriority: opts?.funkPriority,
        }),
      },
      token,
    ),
  getWorld: (token: string, id: string) => req<Snapshot>(`/worlds/${id}`, {}, token),
  placeBuilding: (
    token: string,
    worldId: string,
    body: {
      kind: string;
      subtype: string;
      name: string;
      lat: number;
      lng: number;
      placement: string;
    },
  ) =>
    req<Snapshot>(`/worlds/${worldId}/buildings`, {
      method: "POST",
      body: JSON.stringify(body),
    }, token),
  hire: (token: string, worldId: string, buildingId: string, role = "firefighter") =>
    req<Snapshot>(
      `/worlds/${worldId}/hire`,
      { method: "POST", body: JSON.stringify({ buildingId, role }) },
      token,
    ),
  dispatch: (
    token: string,
    worldId: string,
    incidentId: string,
    vehicleIds: string[],
    mode?: "auto" | "queue" | "divert",
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/dispatch`,
      { method: "POST", body: JSON.stringify({ incidentId, vehicleIds, mode }) },
      token,
    ),
  dispatchByType: (
    token: string,
    worldId: string,
    incidentId: string,
    opts?: {
      byType?: Record<string, number>;
      mode?: "auto" | "queue" | "divert";
      aao?: boolean;
    },
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/dispatch-by-type`,
      {
        method: "POST",
        body: JSON.stringify({
          incidentId,
          byType: opts?.byType,
          mode: opts?.mode,
          aao: opts?.aao,
        }),
      },
      token,
    ),
  takeLoan: (token: string, worldId: string, amount: number) =>
    req<Snapshot>(
      `/worlds/${worldId}/finance/loan`,
      { method: "POST", body: JSON.stringify({ amount }) },
      token,
    ),
  repayLoan: (token: string, worldId: string, amount?: number) =>
    req<Snapshot>(
      `/worlds/${worldId}/finance/repay`,
      { method: "POST", body: JSON.stringify({ amount }) },
      token,
    ),
  cityGrant: (token: string, worldId: string) =>
    req<Snapshot>(`/worlds/${worldId}/finance/grant`, { method: "POST", body: "{}" }, token),
  signContract: (token: string, worldId: string, contractId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/story/sign-contract`,
      { method: "POST", body: JSON.stringify({ contractId }) },
      token,
    ),
  incidentSupport: (
    token: string,
    worldId: string,
    incidentId: string,
    action:
      | "tow"
      | "civil_works"
      | "water_assist"
      | "roadblock"
      | "detour"
      | "traffic_light"
      | "street_clean"
      | "utilities"
      | "thw_consult"
      | "crisis_intervention"
      | "care_tent"
      | "nea_light"
      | "funeral"
      | "pol_patrol"
      | "kripo_scene"
      | "ktu_investigation"
      | "pol_helo",
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/incidents/support`,
      { method: "POST", body: JSON.stringify({ incidentId, action }) },
      token,
    ),
  incidentTactic: (
    token: string,
    worldId: string,
    incidentId: string,
    templateId: string,
    choiceId: string,
    opts?: { triageSorted?: boolean },
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/incidents/tactic`,
      {
        method: "POST",
        body: JSON.stringify({
          incidentId,
          templateId,
          choiceId,
          triageSorted: opts?.triageSorted,
        }),
      },
      token,
    ),
  incidentWater: (
    token: string,
    worldId: string,
    incidentId: string,
    action: "extend_hose" | "setup_suction" | "start_pendel",
    vehicleId?: string,
    lat?: number,
    lng?: number,
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/incidents/water`,
      { method: "POST", body: JSON.stringify({ incidentId, action, vehicleId, lat, lng }) },
      token,
    ),
  incidentMapTacticalUi: (token: string, worldId: string, incidentId: string) =>
    req<{
      mode: string | null;
      phase: string;
      active: boolean;
      hold: boolean;
      tools: string[];
      label: string | null;
      stagingBlocksAlarm: boolean;
    }>(`/worlds/${worldId}/incidents/${incidentId}/map-tactical-ui`, {}, token),
  incidentMapTactical: (
    token: string,
    worldId: string,
    incidentId: string,
    body: {
      action: "start" | "place";
      placementType?: string;
      lat?: number;
      lng?: number;
      vehicleId?: string;
    },
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/incidents/map-tactical`,
      { method: "POST", body: JSON.stringify({ incidentId, ...body }) },
      token,
    ),
  moveBuilding: (token: string, worldId: string, buildingId: string, lat: number, lng: number) =>
    req<Snapshot>(
      `/worlds/${worldId}/buildings/move`,
      { method: "POST", body: JSON.stringify({ buildingId, lat, lng }) },
      token,
    ),
  renameBuilding: (token: string, worldId: string, buildingId: string, name: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/buildings/rename`,
      { method: "POST", body: JSON.stringify({ buildingId, name }) },
      token,
    ),
  demolishBuilding: (token: string, worldId: string, buildingId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/buildings/demolish`,
      { method: "POST", body: JSON.stringify({ buildingId }) },
      token,
    ),
  upgradeBuilding: (token: string, worldId: string, buildingId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/buildings/upgrade`,
      { method: "POST", body: JSON.stringify({ buildingId }) },
      token,
    ),
  buyBuildingModule: (token: string, worldId: string, buildingId: string, moduleId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/buildings/module`,
      { method: "POST", body: JSON.stringify({ buildingId, moduleId }) },
      token,
    ),
  expandBuildingSlots: (
    token: string,
    worldId: string,
    buildingId: string,
    kind: "vehicle" | "trailer" | "ab" | "rc",
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/buildings/slots`,
      { method: "POST", body: JSON.stringify({ buildingId, kind }) },
      token,
    ),
  buyVehicle: (token: string, worldId: string, buildingId: string, type: string, callsign?: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/vehicles/buy`,
      { method: "POST", body: JSON.stringify({ buildingId, type, callsign }) },
      token,
    ),
  renameVehicle: (token: string, worldId: string, vehicleId: string, callsign: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/vehicles/rename`,
      { method: "POST", body: JSON.stringify({ vehicleId, callsign }) },
      token,
    ),
  transferVehicle: (token: string, worldId: string, vehicleId: string, toBuildingId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/vehicles/transfer`,
      { method: "POST", body: JSON.stringify({ vehicleId, toBuildingId }) },
      token,
    ),
  railTransferVehicle: (token: string, worldId: string, vehicleId: string, toBuildingId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/vehicles/rail-transfer`,
      { method: "POST", body: JSON.stringify({ vehicleId, toBuildingId }) },
      token,
    ),
  maintainVehicle: (token: string, worldId: string, vehicleId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/vehicles/maintain`,
      { method: "POST", body: JSON.stringify({ vehicleId }) },
      token,
    ),
  refillVehicle: (token: string, worldId: string, vehicleId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/vehicles/refill`,
      { method: "POST", body: JSON.stringify({ vehicleId }) },
      token,
    ),
  returnVehicle: (token: string, worldId: string, vehicleId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/vehicles/return`,
      { method: "POST", body: JSON.stringify({ vehicleId }) },
      token,
    ),
  convertVehicle: (token: string, worldId: string, vehicleId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/vehicles/convert`,
      { method: "POST", body: JSON.stringify({ vehicleId }) },
      token,
    ),
  decideCitizen: (
    token: string,
    worldId: string,
    requestId: string,
    decision: "accept" | "reject",
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/citizen/decide`,
      { method: "POST", body: JSON.stringify({ requestId, decision }) },
      token,
    ),
  activatePlannedIncident: (
    token: string,
    worldId: string,
    incidentId: string,
    prepDone?: string[],
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/incidents/activate-planned`,
      { method: "POST", body: JSON.stringify({ incidentId, prepDone }) },
      token,
    ),
  transferHospitalPatient: (
    token: string,
    worldId: string,
    patientId: string,
    toBuildingId: string,
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/hospital/transfer`,
      { method: "POST", body: JSON.stringify({ patientId, toBuildingId }) },
      token,
    ),
  transferCustodyToJva: (
    token: string,
    worldId: string,
    detaineeId: string,
    toBuildingId?: string,
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/custody/transfer-jva`,
      {
        method: "POST",
        body: JSON.stringify({ detaineeId, toBuildingId }),
      },
      token,
    ),
  custodySendToCourt: (token: string, worldId: string, detaineeId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/custody/court`,
      { method: "POST", body: JSON.stringify({ detaineeId }) },
      token,
    ),
  custodyRelease: (token: string, worldId: string, detaineeId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/custody/release`,
      { method: "POST", body: JSON.stringify({ detaineeId }) },
      token,
    ),
  deleteWorld: (token: string, worldId: string) =>
    req<{ deleted: number; ok: boolean }>(`/worlds/${worldId}`, { method: "DELETE" }, token),
  verbandStub: (token: string) =>
    req<{
      id: string;
      name: string;
      joinMode: "invite" | "application" | "open";
      memberCap: number;
      memberCapTier: number;
      seasonScore: number;
      rankingPoints: number;
      role: string;
      joinModes: Array<"invite" | "application" | "open">;
    }>("/verband/stub", {}, token),
  patchVerbandStub: (
    token: string,
    body: { joinMode?: "invite" | "application" | "open"; name?: string },
  ) =>
    req<{
      id: string;
      name: string;
      joinMode: "invite" | "application" | "open";
      memberCap: number;
      memberCapTier: number;
      seasonScore: number;
      rankingPoints: number;
      role: string;
      joinModes: Array<"invite" | "application" | "open">;
    }>("/verband/stub", { method: "PATCH", body: JSON.stringify(body) }, token),
  bumpTutorial: (token: string, worldId: string, minStep: number) =>
    req<Snapshot>(
      `/worlds/${worldId}/tutorial/bump`,
      { method: "POST", body: JSON.stringify({ minStep }) },
      token,
    ),
  tick: (token: string, worldId: string) =>
    req<Snapshot>(`/worlds/${worldId}/tick`, { method: "POST", body: "{}" }, token),
  attachLeitstelle: (token: string, worldId: string, buildingId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/buildings/attach-leitstelle`,
      { method: "POST", body: JSON.stringify({ buildingId }) },
      token,
    ),
  resetWorlds: (token: string) =>
    req<{ deleted: number; ok: boolean }>("/worlds/reset", { method: "POST", body: "{}" }, token),
  pois: (lat: number, lng: number, radiusKm: number, opts?: { refresh?: boolean; packs?: string[] }) => {
    const refresh = opts?.refresh ? "&refresh=true" : "";
    const packs = opts?.packs?.length ? `&packs=${encodeURIComponent(opts.packs.join(","))}` : "";
    return req<{
      pois: Array<{
        id: string;
        kind: string;
        name: string;
        lat: number;
        lng: number;
        detail?: string;
      }>;
      lines: Array<{
        id: string;
        kind: string;
        name: string;
        coordinates: [number, number][];
      }>;
      warning?: string;
    }>(`/meta/pois?lat=${lat}&lng=${lng}&radiusKm=${radiusKm}${refresh}${packs}`);
  },
  hydrants: (
    token: string,
    worldId: string,
    opts?: { lat?: number; lng?: number; radiusKm?: number },
  ) => {
    const q = new URLSearchParams();
    if (opts?.lat != null) q.set("lat", String(opts.lat));
    if (opts?.lng != null) q.set("lng", String(opts.lng));
    if (opts?.radiusKm != null) q.set("radiusKm", String(opts.radiusKm));
    const qs = q.toString() ? `?${q}` : "";
    return req<{
      zone: string;
      note: string;
      pois: Array<{
        id: string;
        kind: string;
        name: string;
        lat: number;
        lng: number;
        source?: string;
      }>;
    }>(`/worlds/${worldId}/hydrants${qs}`, {}, token);
  },
  unlockCompetence: (token: string, worldId: string, competenceId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/competences/unlock`,
      { method: "POST", body: JSON.stringify({ competenceId }) },
      token,
    ),
  competences: () =>
    req<{
      defs: Array<{
        id: string;
        title: string;
        body: string;
        branch?: string;
        requires?: string[];
        minLevel: number;
        costXp: number;
        costInfluence: number;
        order?: number;
      }>;
      branches?: Record<string, string>;
    }>("/meta/competences"),
  trainPersonnel: (token: string, worldId: string, personnelId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/personnel/train`,
      { method: "POST", body: JSON.stringify({ personnelId }) },
      token,
    ),
  setPersonnelRole: (token: string, worldId: string, personnelId: string, role: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/personnel/role`,
      { method: "POST", body: JSON.stringify({ personnelId, role }) },
      token,
    ),
  assignPersonnel: (token: string, worldId: string, personnelId: string, vehicleId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/personnel/assign`,
      { method: "POST", body: JSON.stringify({ personnelId, vehicleId }) },
      token,
    ),
  unassignPersonnel: (token: string, worldId: string, personnelId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/personnel/unassign`,
      { method: "POST", body: JSON.stringify({ personnelId }) },
      token,
    ),
  autoAssignCrew: (
    token: string,
    worldId: string,
    opts: { buildingId?: string; vehicleId?: string },
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/personnel/auto-assign`,
      { method: "POST", body: JSON.stringify(opts) },
      token,
    ),
  ensureMinDutyCrew: (token: string, worldId: string, opts?: { buildingId?: string }) =>
    req<{ snapshot: Snapshot; woken: number }>(
      `/worlds/${worldId}/personnel/ensure-duty`,
      { method: "POST", body: JSON.stringify(opts ?? {}) },
      token,
    ),
  recruitCampaign: (
    token: string,
    worldId: string,
    kind: "flyer" | "ad" | "campaign",
    org: "fire" | "police" | "ems" | "thw",
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/recruit/campaign`,
      { method: "POST", body: JSON.stringify({ kind, org }) },
      token,
    ),
  inviteApplicantInterview: (token: string, worldId: string, applicantId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/recruit/interview`,
      { method: "POST", body: JSON.stringify({ applicantId }) },
      token,
    ),
  hireApplicant: (token: string, worldId: string, applicantId: string, buildingId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/recruit/hire`,
      { method: "POST", body: JSON.stringify({ applicantId, buildingId }) },
      token,
    ),
  rejectApplicant: (token: string, worldId: string, applicantId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/recruit/reject`,
      { method: "POST", body: JSON.stringify({ applicantId }) },
      token,
    ),
  setPersonnelShift: (
    token: string,
    worldId: string,
    personnelId: string,
    status: "idle" | "off_duty",
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/personnel/shift`,
      { method: "POST", body: JSON.stringify({ personnelId, status }) },
      token,
    ),
  finance: (token: string, worldId: string) =>
    req<{
      recent: Array<{
        id: string;
        currency: string;
        amount: number;
        category: string;
        label: string;
        createdAt: string | number;
      }>;
      today: Record<
        string,
        { byCategory: Record<string, number>; income: number; expense: number; net: number }
      >;
      allTime: Record<
        string,
        { byCategory: Record<string, number>; income: number; expense: number; net: number }
      >;
    }>(`/worlds/${worldId}/finance`, {}, token),
  launchImageCampaign: (token: string, worldId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/finance/image-campaign`,
      { method: "POST", body: JSON.stringify({}) },
      token,
    ),
  adminStats: (
    token: string,
    worldId: string,
    body: {
      budget?: number;
      renown?: number;
      influence?: number;
      playerLevel?: number;
      xp?: number;
    },
  ) =>
    req<Snapshot>(
      `/worlds/${worldId}/admin/stats`,
      { method: "POST", body: JSON.stringify(body) },
      token,
    ),
  adminBoostLate: (token: string, worldId: string) =>
    req<Snapshot>(`/worlds/${worldId}/admin/boost-late`, { method: "POST", body: "{}" }, token),
  claimDailyLogin: (token: string, worldId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/rewards/daily-login`,
      { method: "POST", body: "{}" },
      token,
    ),
  adminSpawn: (token: string, worldId: string, templateId?: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/admin/spawn-incident`,
      { method: "POST", body: JSON.stringify({ templateId }) },
      token,
    ),
  customIncidentsList: (token: string) =>
    req<{ items: CustomIncidentItem[] }>("/custom-incidents", {}, token),
  customIncidentsPending: (token: string) =>
    req<{ items: CustomIncidentItem[] }>("/custom-incidents/pending", {}, token),
  customIncidentCreate: (token: string, draft?: Partial<CustomIncidentDraft>) =>
    req<{ item: CustomIncidentItem }>("/custom-incidents", {
      method: "POST",
      body: JSON.stringify({ draft }),
    }, token),
  customIncidentGet: (token: string, id: string) =>
    req<{ item: CustomIncidentItem }>(`/custom-incidents/${encodeURIComponent(id)}`, {}, token),
  customIncidentSave: (
    token: string,
    id: string,
    body: {
      draft?: Partial<CustomIncidentDraft>;
      status?: "draft" | "published" | "pending_review";
      notes?: string;
    },
  ) =>
    req<{ item: CustomIncidentItem }>(`/custom-incidents/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }, token),
  customIncidentDelete: (token: string, id: string) =>
    req<{ ok: boolean }>(`/custom-incidents/${encodeURIComponent(id)}`, { method: "DELETE" }, token),
  customIncidentDuplicate: (token: string, id: string) =>
    req<{ item: CustomIncidentItem }>(
      `/custom-incidents/${encodeURIComponent(id)}/duplicate`,
      { method: "POST", body: "{}" },
      token,
    ),
  customIncidentSuggest: (
    token: string,
    body: Partial<CustomIncidentDraft> & { playerLevel?: number },
  ) =>
    req<CustomIncidentSuggestResult>("/custom-incidents/suggest", {
      method: "POST",
      body: JSON.stringify(body),
    }, token),
  customIncidentSpawnTest: (token: string, worldId: string, customId: string) =>
    req<Snapshot>(
      `/worlds/${worldId}/custom-incidents/${encodeURIComponent(customId)}/spawn`,
      { method: "POST", body: "{}" },
      token,
    ),
  adminCustomIncidentReview: (
    token: string,
    id: string,
    body: {
      status: "pending_review" | "approved" | "rejected";
      reviewNotes?: string;
      mergeToCatalog?: boolean;
    },
  ) =>
    req<{ item: CustomIncidentItem }>(`/admin/custom-incidents/${encodeURIComponent(id)}/review`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }, token),

  customVehiclesList: (token: string) =>
    req<{ items: CustomVehicleItem[]; buildingKinds: Array<{ id: string; label: string }> }>(
      "/custom-vehicles",
      {},
      token,
    ),
  customVehicleCreate: (token: string, draft?: Partial<CustomVehicleDraft>) =>
    req<{ item: CustomVehicleItem }>("/custom-vehicles", {
      method: "POST",
      body: JSON.stringify({ draft }),
    }, token),
  customVehicleSave: (
    token: string,
    id: string,
    body: {
      draft?: Partial<CustomVehicleDraft>;
      status?: "draft" | "published" | "pending_review";
      notes?: string;
    },
  ) =>
    req<{ item: CustomVehicleItem }>(`/custom-vehicles/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }, token),
  customVehicleDelete: (token: string, id: string) =>
    req<{ ok: boolean }>(`/custom-vehicles/${encodeURIComponent(id)}`, { method: "DELETE" }, token),
  customVehicleDuplicate: (token: string, id: string) =>
    req<{ item: CustomVehicleItem }>(
      `/custom-vehicles/${encodeURIComponent(id)}/duplicate`,
      { method: "POST", body: "{}" },
      token,
    ),
  customVehicleSuggest: (token: string, body: Partial<CustomVehicleDraft>) =>
    req<{ suggestions: Partial<CustomVehicleDraft>; explanation: string[] }>(
      "/custom-vehicles/suggest",
      { method: "POST", body: JSON.stringify(body) },
      token,
    ),

  customTrainingsList: (token: string) =>
    req<{
      items: CustomTrainingItem[];
      roles: Array<{ id: string; label: string }>;
    }>("/custom-trainings", {}, token),
  customTrainingOptions: (token: string) =>
    req<{ options: Array<{ id: string; label: string; org: string }> }>(
      "/custom-trainings/options",
      {},
      token,
    ),
  customTrainingCreate: (token: string, draft?: Partial<CustomTrainingDraft>) =>
    req<{ item: CustomTrainingItem }>("/custom-trainings", {
      method: "POST",
      body: JSON.stringify({ draft }),
    }, token),
  customTrainingSave: (
    token: string,
    id: string,
    body: {
      draft?: Partial<CustomTrainingDraft>;
      status?: "draft" | "published" | "pending_review";
      notes?: string;
    },
  ) =>
    req<{ item: CustomTrainingItem }>(`/custom-trainings/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }, token),
  customTrainingDelete: (token: string, id: string) =>
    req<{ ok: boolean }>(`/custom-trainings/${encodeURIComponent(id)}`, { method: "DELETE" }, token),
  customTrainingDuplicate: (token: string, id: string) =>
    req<{ item: CustomTrainingItem }>(
      `/custom-trainings/${encodeURIComponent(id)}/duplicate`,
      { method: "POST", body: "{}" },
      token,
    ),
  customTrainingSuggest: (token: string, body: Partial<CustomTrainingDraft>) =>
    req<{ suggestions: Partial<CustomTrainingDraft>; explanation: string[] }>(
      "/custom-trainings/suggest",
      { method: "POST", body: JSON.stringify(body) },
      token,
    ),

  customEventsList: (token: string) =>
    req<{
      items: CustomEventItem[];
      families: Array<{ id: string; label: string }>;
      worldKinds: Array<{ id: string; label: string }>;
    }>("/custom-events", {}, token),
  customEventCreate: (token: string, draft?: Partial<CustomEventDraft>) =>
    req<{ item: CustomEventItem }>("/custom-events", {
      method: "POST",
      body: JSON.stringify({ draft }),
    }, token),
  customEventSave: (
    token: string,
    id: string,
    body: {
      draft?: Partial<CustomEventDraft>;
      status?: CustomEventStatus;
      notes?: string;
    },
  ) =>
    req<{ item: CustomEventItem }>(`/custom-events/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }, token),
  customEventDelete: (token: string, id: string) =>
    req<{ ok: boolean }>(`/custom-events/${encodeURIComponent(id)}`, { method: "DELETE" }, token),
  customEventDuplicate: (token: string, id: string) =>
    req<{ item: CustomEventItem }>(
      `/custom-events/${encodeURIComponent(id)}/duplicate`,
      { method: "POST", body: "{}" },
      token,
    ),

  catalog: () =>
    req<{
      vehicles: Record<string, string>;
      vehiclePrices: Record<string, number>;
      vehicleDefs?: Array<{
        id: string;
        label: string;
        org?: string;
        price?: number;
      }>;
      vehicleSizes: Record<string, string>;
      vehicleSizeTemplates: Record<
        string,
        { id: string; label: string; w: number; h: number; note: string }
      >;
      incidents: Array<{
        id: string;
        title: string;
        severity: number;
        minLevel: number;
        sizeClass: string;
        rewardBudget: number;
        required: Record<string, number>;
      }>;
      levelUnlocks?: Array<{ level: number; title: string; body: string }>;
    }>("/meta/catalog"),
  autoWiki: () =>
    req<{ entries: Array<{ id: string; title: string; body: string; auto?: boolean }> }>(
      "/meta/wiki",
    ),
  wsUrl: (worldId: string) => {
    if (API.startsWith("http")) {
      return `${API.replace(/^http/, "ws")}/ws/${worldId}`;
    }
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${window.location.host}/ws/${worldId}`;
  },
};
